import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  ListSubheader,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { PLACE_ID_PATTERN, PLACES_AUTOCOMPLETE_LIMITS } from '@bandido/shared/types';

import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { connectorCapabilitiesAreActionable } from '@frontend/features/rutero/connectorCapabilitiesQueryKey';
import { CONFIG } from '@frontend/config';
import {
  CatalogEntry,
  ConnectorInfo,
  PricedSearchResult,
  useConnectorCapabilities,
  useEsimDestinations,
  usePlacesAutocomplete,
  useSearchEsims,
} from '../queries';
import { committedParty } from '../committedBookingContext';
import { placesNoticeReason } from '../places';
import {
  cleanParams,
  isIncompleteSearch,
  UrlParams,
  useSearchUrlState,
} from '../useSearchUrlState';
import { describeEsims } from '../describeSearch';
import { deriveServedByForVertical } from '../connectorCapabilityResolution';
import { usePlacesEnabled } from '../usePlacesEnabled';
import { useAutocompleteFocus } from '../useAutocompleteFocus';
import { SearchBar, SearchField } from './SearchBar';
import { RecentSearches } from './RecentSearches';
import {
  destinationLabel,
  esimsDestinationOptions,
  isCountryEntry,
  placeToDestinationEntry,
} from '../destinations';
import { esimsSearchPayloadFromParams, parseDataGb } from '../esims';
import { EsimsResultsTable } from './EsimsResultsTable';
import { BookingModal } from './BookingModal';
import { quoteSearchIdentity, SelectedTravelQuote } from '../selectedQuote';
import {
  captureSelectableSearchQuote,
  searchFormDiverged,
  shouldRefetchExpiredIdenticalSearch,
} from '../searchResultAuthority';
import { useFareCountdown } from '../useFareCountdown';
import { useSearchResultAuthority } from '../useSearchResultAuthority';
import { SearchResultGenerationStatus } from './SearchResultGenerationStatus';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ActiveSearchSummary } from './ActiveSearchSummary';

const DATA_OPTIONS = [1, 3, 5, 10, 20, 50];
const VALIDITY_OPTIONS = [3, 7, 15, 30, 60, 90];

/** Chip toggle de filtro (mismo patrón visual que el portal del proveedor). */
function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Chip
      label={label}
      size="small"
      color={active ? 'primary' : 'default'}
      variant={active ? 'filled' : 'outlined'}
      onClick={onClick}
    />
  );
}

/**
 * Buscador de eSIMs: barra de búsqueda arriba y filtros del resultado en una
 * fila compacta, con la tabla a ancho completo (los paquetes traen coberturas
 * largas). Sin fechas ni pax: un paquete es país/región + datos + vigencia
 * (que arranca al ACTIVAR la eSIM).
 *
 * UN SOLO control con DOS fuentes (#2174 — las regiones NO son lugares,
 * ubikeishon no las tiene): los
 * PAÍSES salen del maestro de lugares (`usePlacesAutocomplete`,
 * `kinds: ['country']`, búsqueda remota rankeada por ubikeishon) y las
 * REGIONES siguen saliendo del catálogo del hub (`useEsimDestinations`,
 * filtrado local). No existe fallback de países al catálogo nativo.
 *
 * El catálogo del hub se trae COMPLETO una vez (es estático y cacheado) y se
 * filtra local: así el autocomplete matchea el nombre traducido al idioma del
 * usuario, no solo el inglés del proveedor. El tipo local/global se DERIVA del
 * destino elegido — sin combinaciones inválidas. Los filtros de datos/vigencia
 * son CLIENT-SIDE sobre los resultados ya traídos: refinar no vuelve al hub.
 */
export function EsimsSearchForm() {
  const { t, i18n } = useTranslation();
  const url = useSearchUrlState({ vertical: 'esims' });
  const searchPayload = useMemo(
    () => esimsSearchPayloadFromParams(url.params),
    [url.params],
  );
  const search = useSearchEsims(searchPayload);
  /** Vigencia de la generación que se está mostrando (§ barra de resumen). */
  const searchCountdown = useFareCountdown(
    search.data ? search.dataUpdatedAt : undefined,
  );
  const activeSearchIdentity = searchPayload
    ? quoteSearchIdentity('esims', CONFIG.AGENCY_ID, searchPayload)
    : undefined;
  const [destination, setDestination] = useState<CatalogEntry | null>(() =>
    url.params.tipo === 'local' &&
    url.params.destino &&
    PLACE_ID_PATTERN.test(url.params.destino)
      ? placeToDestinationEntry({
        id: url.params.destino,
        name: url.params.destinoNombre ?? url.params.destino,
      })
      : url.params.tipo === 'global' && url.params.region
        ? { id: url.params.region, name: url.params.destinoNombre ?? url.params.region, type: 'region' }
        : null);
  const [debouncedQuery, setQuery] = useDebouncedState('', 250);
  const [minGb, setMinGb] = useState<number | null>(null);
  const [maxDays, setMaxDays] = useState<number | null>(null);
  const [onlyUnlimited, setOnlyUnlimited] = useState(false);
  const [bookingQuote, setBookingQuote] = useState<
    SelectedTravelQuote<PricedSearchResult> | null
  >(null);
  const bookingOffer = bookingQuote?.offer ?? null;

  const {language} = i18n;
  // Foco/blur compartido con el resto de los pickers del buscador (#2226):
  // enfocar con destino elegido deja tipear otro de una, y salir sin elegir
  // devuelve el anterior. `destinationLabel` es la MISMA etiqueta que
  // `getOptionLabel` de abajo — si difirieran, MUI repondría el input contra
  // la suya.
  const focus = useAutocompleteFocus({
    selectedLabel: destination ? destinationLabel(destination, language) : '',
    onQueryChange: setQuery,
  });
  const { data: catalog, isLoading: loadingCatalog } = useEsimDestinations();

  /**
   * Países por el maestro de lugares y regiones por el catálogo del hub. No
   * hay fallback a países ISO: si Places falla, las regiones siguen
   * disponibles y Bandido no fabrica una búsqueda local desde otro código.
   */
  const placesSettingEnabled = usePlacesEnabled() === true;
  const {
    data: connectors,
    isSuccess: capabilitiesLoaded,
    isFetching: capabilitiesFetching,
  } = useConnectorCapabilities();
  const capabilitiesActionable = connectorCapabilitiesAreActionable({
    isSuccess: capabilitiesLoaded,
    isFetching: capabilitiesFetching,
  });
  const servedBy = useMemo(
    () => deriveServedByForVertical(connectors as ConnectorInfo[] | undefined, 'esims'),
    [connectors],
  );
  const placesReady =
    placesSettingEnabled && capabilitiesActionable && servedBy.length > 0;
  const {
    data: placesResult,
    isFetching: searchingPlaces,
    isError: placesFailed,
  } = usePlacesAutocomplete(debouncedQuery, {
    kinds: ['country'],
    servedBy,
    enabled: placesReady,
  });
  // Aislado (#2226): el estado de carga de ESTE campo se enciende solo
  // mientras el asesor tipea acá — ni el catálogo cargando de fondo al abrir
  // la pantalla, ni ninguna otra búsqueda, lo prenden por él.
  const showDestinationLoading =
    focus.editing &&
    ((loadingCatalog && !destination) ||
      (placesReady &&
        searchingPlaces &&
        debouncedQuery.trim().length >= PLACES_AUTOCOMPLETE_LIMITS.queryMinLength));
  // MISMA función que usa `PlaceAutocomplete` (21.22), no un aviso paralelo:
  // sin esto, un fallo del maestro deja `placeCountries` vacío y la lista
  // muestra solo regiones — indistinguible de "no hay países", con TODOS los
  // eSIM de país imposibles de buscar y sin decir por qué.
  const placesNotice = placesReady
    ? placesNoticeReason({
      ready: placesReady,
      queryLength: debouncedQuery.trim().length,
      minLength: PLACES_AUTOCOMPLETE_LIMITS.queryMinLength,
      isError: placesFailed,
    })
    : undefined;
  // El aviso va en `helperText`, no en `noOptionsText`: las REGIONES siguen
  // en la lista aunque los países fallen, así que el popup nunca está vacío
  // y `noOptionsText` no se vería nunca.
  const placeCountries = useMemo(
    () => (placesResult?.results ?? []).map(placeToDestinationEntry),
    [placesResult],
  );

  const committedDestination = useMemo(() => {
    if (!searchPayload) return null;
    const id = searchPayload.destination ?? searchPayload.region;
    if (!id) return null;
    if (searchPayload.destination) {
      // Places: nunca sale del catálogo nativo (es un plc_*, no el id/
      // countryCode de ese catálogo) — el nombre para mostrar sale de la URL.
      return placeToDestinationEntry({ id, name: url.params.destinoNombre ?? id });
    }
    const catalogEntry = (catalog ?? []).find((entry) => entry.id === id);
    return (
      catalogEntry ?? {
        id,
        name: url.params.destinoNombre ?? id,
        type: 'region',
      }
    );
  }, [catalog, searchPayload, url.params.destinoNombre]);

  // Países primero y regiones al final ("Also available in" del portal), con
  // los nombres en el idioma del usuario. Con PLACES listo, los países salen
  // de `placeCountries` (ya rankeados por ubikeishon — no se re-filtran
  // localmente, mismo criterio que el resto del epic) y las regiones siguen
  // saliendo del catálogo, filtradas local igual que siempre.
  const options = useMemo(
    () =>
      esimsDestinationOptions({
        placesReady,
        placeCountries,
        catalog,
        query: focus.query,
        language,
      }),
    [placesReady, placeCountries, catalog, focus.query, language],
  );

  const type: 'local' | 'global' = isCountryEntry(destination)
    ? 'local'
    : 'global';

  /** Vacía la búsqueda: campos, URL y memoria de navegación. */
  const handleClear = () => {
    setDestination(null);
    setQuery('');
    url.reset();
  };

  /**
   * El formulario tal como se escribiría en la URL. Es la ÚNICA derivación del
   * borrador — de acá salen la búsqueda que se confirma y el payload contra el
   * que se compara la comprometida (21.22).
   *
   * El hub recibe país por Places (`plc_*`) o región (slug). Las keys del
   * formato no elegido viajan en `undefined`: `url.commit` mergea y hay que
   * borrar lo que dejó una búsqueda o URL anterior.
   */
  const formParams: UrlParams = !destination
    ? { buscar: '1', destino: undefined, pais: undefined, region: undefined }
    : PLACE_ID_PATTERN.test(destination.id)
      ? {
        buscar: '1',
        destino: destination.id,
        destinoNombre: destination.name,
        tipo: 'local',
        pais: undefined,
        region: undefined,
      }
      : destination.type === 'region'
        ? {
          buscar: '1',
          region: destination.id,
          destinoNombre: destination.name,
          tipo: 'global',
          pais: undefined,
          destino: undefined,
        }
        : { buscar: '1', destino: undefined, pais: undefined, region: undefined };
  const draftPayload = esimsSearchPayloadFromParams(cleanParams(formParams));
  const formDiverged = searchFormDiverged(searchPayload, draftPayload);
  const resultAuthority = useSearchResultAuthority(search, formDiverged);

  const handleSearch = () => {
    if (!destination) return;
    const nextIdentity = draftPayload
      ? quoteSearchIdentity('esims', CONFIG.AGENCY_ID, draftPayload)
      : undefined;
    if (
      shouldRefetchExpiredIdenticalSearch(
        activeSearchIdentity,
        nextIdentity,
        Boolean(search.data),
        search.dataUpdatedAt,
        Date.now(),
      )
    ) {
      void search.refetch();
    }
    url.commit(formParams);
  };

  const filteredResults = useMemo(() => {
    const results = search.data?.results ?? [];
    return results.filter((result) => {
      if (onlyUnlimited && !result.unlimited) return false;
      if (minGb !== null && parseDataGb(result) < minGb) return false;
      if (
        maxDays !== null &&
        typeof result.validityDays === 'number' &&
        result.validityDays > maxDays
      ) {
        return false;
      }
      return true;
    });
  }, [search.data, minGb, maxDays, onlyUnlimited]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Barra de búsqueda horizontal: la tabla usa todo el ancho. */}
      <SearchBar
        onClear={url.hasParams ? handleClear : undefined}
        onSearch={handleSearch}
        loading={search.isLoading}
        disabled={!destination}
      >
        <SearchField label={t('travelServices.esims.destination')} grow width={340}>
          <Autocomplete
            size="small"
            sx={{ width: '100%' }}
            options={options}
            loading={showDestinationLoading}
            value={destination}
            onChange={(_event, value, reason) => {
              if (focus.commitValueChange(reason)) setDestination(value);
            }}
            inputValue={focus.inputValue}
            onFocus={focus.onFocus}
            onBlur={focus.onBlur}
            onInputChange={focus.onInputChange}
            getOptionLabel={(option) => destinationLabel(option, language)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            // El filtrado ya lo hace matchesDestination (nombre traducido +
            // original + código): MUI no debe re-filtrar por el label.
            filterOptions={(opts) => opts}
            groupBy={(option) =>
              isCountryEntry(option)
                ? t('travelServices.esims.countries')
                : t('travelServices.esims.regions')
            }
            renderGroup={(params) => (
              <li key={params.key}>
                <ListSubheader sx={{ lineHeight: '32px' }}>
                  {params.group}
                </ListSubheader>
                {params.children}
              </li>
            )}
            noOptionsText={t('travelServices.esims.noDestinations')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('travelServices.esims.destination')}
                placeholder={t('travelServices.esims.destinationPlaceholder')}
                required
                error={placesNotice === 'error'}
                helperText={
                  placesNotice === 'error'
                    ? t('travelServices.esims.countriesUnavailable')
                    : undefined
                }
              />
            )}
          />
        </SearchField>
        {destination && (
          <Chip
            size="small"
            variant="outlined"
            sx={{ alignSelf: 'center', mr: 2 }}
            label={t('travelServices.esims.coverageIs', {
              type: t(`travelServices.esims.${type}`),
            })}
          />
        )}
      </SearchBar>

      {url.params.buscar !== '1' && (
        <RecentSearches
          vertical="esims"
          describe={(params) => describeEsims(params, t, i18n.language)}
        />
      )}

      {/* Filtros del resultado: solo cuando hay algo que refinar. */}
      {search.data && !search.isLoading && (
        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          px={2}
          py={1.5}
          bgcolor="grey.100"
          borderRadius={1}
        >
          <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary" mr={0.5}>
              {t('travelServices.esims.dataFilter')}
            </Typography>
            {DATA_OPTIONS.map((gb) => (
              <FilterChip
                key={gb}
                label={`${gb}+`}
                active={minGb === gb}
                onClick={() => setMinGb(minGb === gb ? null : gb)}
              />
            ))}
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box display="flex" gap={0.75} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary" mr={0.5}>
              {t('travelServices.esims.validityFilter')}
            </Typography>
            {VALIDITY_OPTIONS.map((days) => (
              <FilterChip
                key={days}
                label={`≤ ${days}`}
                active={maxDays === days}
                onClick={() => setMaxDays(maxDays === days ? null : days)}
              />
            ))}
          </Box>
          <Divider orientation="vertical" flexItem />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={onlyUnlimited}
                onChange={(event) => setOnlyUnlimited(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {t('travelServices.esims.unlimited')}
              </Typography>
            }
          />
        </Box>
      )}

      {/* La URL pide una búsqueda que no se puede ejecutar (link
          recortado o editado a mano): decirlo, no quedarse mudo. */}
      {isIncompleteSearch(url.params, searchPayload) && (
        <Alert severity="warning">{t('travelServices.incompleteLink')}</Alert>
      )}

      <SearchResultGenerationStatus authority={resultAuthority} />

      {url.params.buscar === '1' && searchPayload && (
        <ActiveSearchSummary
          label={t('travelServices.activeSearchSummary')}
          lines={[describeEsims(url.params, t, i18n.language)]}
          countdown={searchCountdown}
          onRefresh={() => void search.refetch()}
        />
      )}

      {search.isLoading && <ResultsSkeleton archetype="table" />}
      {search.isError && <Alert severity="error">{t('travelServices.searchError')}</Alert>}
      {!search.data && !search.isLoading && !search.isError && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.esims.emptyState')}
        </Typography>
      )}
      {search.data && !search.isLoading && (
        <EsimsResultsTable
          results={filteredResults}
          providers={search.data.providers}
          bookingDisabled={!resultAuthority.canSelectResult}
          onBook={(offer) =>
            setBookingQuote(
              captureSelectableSearchQuote(
                offer,
                search.dataUpdatedAt,
                'esims',
                CONFIG.AGENCY_ID,
                searchPayload,
                Date.now(),
                formDiverged,
              ),
            )
          }
        />
      )}

      <BookingModal
        vertical="esims"
        quote={bookingQuote}
        activeSearchIdentity={activeSearchIdentity}
        details={[
          `${
            committedDestination
              ? destinationLabel(committedDestination, language)
              : ''
          } · ${t(
            `travelServices.esims.${searchPayload?.type ?? 'local'}`,
          )}${bookingOffer?.dataAmount ? ` · ${bookingOffer.dataAmount}` : ''}${
            bookingOffer?.validityDays
              ? ` · ${t('travelServices.esims.validity', {
                count: bookingOffer.validityDays,
              })}`
              : ''
          }`,
        ]}
        party={committedParty({ vertical: 'esims', payload: searchPayload })}
        onClose={() => setBookingQuote(null)}
      />
    </Box>
  );
}
